import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { useUnits } from '../../contexts/UnitsContext';
import Navbar from '../Layout/Navbar';
import './RaceHistoryPage.css';

interface RaceRecord {
  id: number;
  race_name: string;
  race_date: string;
  race_distance_miles: number;
  goal_finish_seconds: number | null;
  predicted_p50_seconds_at_creation: number | null;
  predicted_p50_seconds_at_race: number | null;
  actual_finish_seconds: number | null;
  actual_pace_sec_per_mile: number | null;
  matched_workout_id: number | null;
  plan_start_date: string | null;
  training_total_miles: number;
  training_weeks: number;
  status: 'completed' | 'expired' | 'abandoned';
  methodology: string | null;
}

const fmtTime = (sec: number | null | undefined): string => {
  if (sec == null) return '—';
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = Math.floor(sec % 60);
  if (h > 0) return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const fmtPace = (sec: number | null | undefined): string => {
  if (sec == null) return '—';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, '0')}/mi`;
};

const fmtDelta = (predicted: number | null, actual: number | null) => {
  if (predicted == null || actual == null) return { text: '—', cls: '' };
  const diff = actual - predicted;
  if (Math.abs(diff) < 30) return { text: 'on target', cls: 'rh-delta-neutral' };
  const abs = Math.abs(diff);
  const m = Math.floor(abs / 60);
  const s = Math.floor(abs % 60);
  const sign = diff < 0 ? '−' : '+';
  const label = diff < 0 ? 'faster' : 'slower';
  const cls = diff < 0 ? 'rh-delta-good' : 'rh-delta-bad';
  return { text: `${sign}${m}:${s.toString().padStart(2, '0')} ${label}`, cls };
};

const STATUS_LABELS: Record<string, string> = {
  completed: 'Completed',
  expired: 'No result synced',
  abandoned: 'Abandoned',
};

const RaceHistoryPage: React.FC = () => {
  const [races, setRaces] = useState<RaceRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { formatDist } = useUnits();

  useEffect(() => {
    api.trackEvent('race_history_viewed');
    (async () => {
      try {
        const data = await api.getRaceHistory();
        setRaces(data.races || []);
      } catch (e: any) {
        setError(e.message || 'Failed to load race history');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <>
    <Navbar />
    <div className="rh-page">
      <div className="rh-container">
        <header className="rh-header">
          <h1>Race History</h1>
          <p className="rh-subtitle">
            Every race you've trained for with My Next Run — predictions, results, and progression over time.
          </p>
        </header>

        {loading && <div className="rh-loading">Loading…</div>}
        {error && <div className="rh-error">{error}</div>}

        {!loading && !error && races.length === 0 && (
          <div className="rh-empty">
            <div className="rh-empty-icon">🏁</div>
            <h2>No races yet</h2>
            <p>Complete your first race and your training history will start tracking here.</p>
          </div>
        )}

        {!loading && races.length > 0 && (
          <div className="rh-table-wrap">
            <table className="rh-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Race</th>
                  <th>Distance</th>
                  <th>Goal</th>
                  <th>Predicted (race day)</th>
                  <th>Actual</th>
                  <th>Δ vs prediction</th>
                  <th>Pace</th>
                  <th>Training</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {races.map(r => {
                  const delta = fmtDelta(r.predicted_p50_seconds_at_race, r.actual_finish_seconds);
                  return (
                    <tr key={r.id}>
                      <td>{r.race_date}</td>
                      <td className="rh-race-name">
                        {r.race_name}
                        {r.predicted_p50_seconds_at_creation != null && r.predicted_p50_seconds_at_race != null &&
                          r.predicted_p50_seconds_at_creation !== r.predicted_p50_seconds_at_race && (
                          <div className="rh-prediction-shift">
                            Started predicting {fmtTime(r.predicted_p50_seconds_at_creation)} → {fmtTime(r.predicted_p50_seconds_at_race)}
                          </div>
                        )}
                      </td>
                      <td>{formatDist(r.race_distance_miles)}</td>
                      <td>{fmtTime(r.goal_finish_seconds)}</td>
                      <td>{fmtTime(r.predicted_p50_seconds_at_race)}</td>
                      <td className="rh-actual">{fmtTime(r.actual_finish_seconds)}</td>
                      <td className={delta.cls}>{delta.text}</td>
                      <td>{fmtPace(r.actual_pace_sec_per_mile)}</td>
                      <td>
                        {formatDist(r.training_total_miles)}
                        <div className="rh-training-weeks">{r.training_weeks} weeks</div>
                      </td>
                      <td>
                        <span className={`rh-status rh-status-${r.status}`}>
                          {STATUS_LABELS[r.status] || r.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default RaceHistoryPage;
